import { ApiService, env } from '@/config/app';
import { checkTokenExpired, clearCookieUserAuthenticated, getCookie, setCookieUserAuthenticated } from './cookie.util';
import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from '@/constants';
import authService from '@/services/AuthService';
import { stringify } from 'qs';
import { handleError as handleErrorAndShowToast } from '@/utils/handleError';
import { ApiErrorResponse } from '@/common/Response';

export interface ApiOptions {
  locale?: string;
  headers?: HeadersInit;
  replaceHeaders?: HeadersInit;
  queryParams?: object;
  isExternalUrl?: boolean;
  disableXBrandId?: boolean;
  customToken?: string;
  isBlob?: boolean;
}
let isRefreshingToken = false;
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function redirectLogin() {
  window.location.replace('/login');
}

export async function getAndRefreshToken(): Promise<any> {
  let accessToken = getCookie(ACCESS_TOKEN_KEY);
  if (accessToken) {
    const isExpired = checkTokenExpired(accessToken);
    if (isExpired) {
      const refreshToken = getCookie(REFRESH_TOKEN_KEY);
      if (!refreshToken || checkTokenExpired(refreshToken)) {
        clearCookieUserAuthenticated();
        redirectLogin();
      }

      if (!isRefreshingToken) {
        isRefreshingToken = true;
        const { data } = await authService.refreshToken(accessToken, refreshToken).catch(() => ({ data: undefined }));
        if (data && data.accessToken && data.refreshToken) {
          setCookieUserAuthenticated(data.accessToken, data.refreshToken);
          accessToken = data.accessToken;
          isRefreshingToken = false;
        } else {
          clearCookieUserAuthenticated();
          isRefreshingToken = false;
          redirectLogin();
        }
      } else {
        await delay(200);
        return await getAndRefreshToken();
      }
    }
  }
  return accessToken;
}

async function handleResponse(response: Response): Promise<any> {
  const result = await response.json();
  if (response.status >= 400) {
    if (response.status === 401) {
    } else if (response.status === 403) {
    }
    throw { httpCode: response.status, ...result };
  }
  return result;
}

async function handleResponseBlob(response: Response): Promise<any> {
  const result = await response.blob();
  if (response.status >= 400) {
    if (response.status === 401) {
    } else if (response.status === 403) {
    }
    throw { httpCode: response.status, ...result };
  }
  return result;
}

async function handleError(error: ApiErrorResponse) {
  if (error.httpCode === 401 || error?.statusCode === 401) {
    clearCookieUserAuthenticated();
    redirectLogin();
    return;
  }

  handleErrorAndShowToast(error);
  throw error;

  // if (error.httpCode >= 500) {
  //   console.error(error);
  //   throw new Error('Sorry, something went wrong.');
  // } else if (error.httpCode === 401) {
  //   clearCookieUserAuthenticated();
  //   redirectLogin();
  // } else {
  //   throw error;
  // }
}

async function handleHeaders(options?: ApiOptions, isCheck: boolean = false): Promise<HeadersInit> {
  if (options?.replaceHeaders) {
    return options.replaceHeaders;
  }
  let accessToken = await getAndRefreshToken();
  if (options?.customToken) {
    accessToken = options?.customToken;
  }

  const xBrandId = !options?.disableXBrandId
    ? {
        'x-brand-id': getCookie(env.BrandX) ?? '',
        // 'x-brand-id': '72f511e1-717f-485c-bdc9-36cfff8cd0b4',
      }
    : undefined;

  let objectHeaderReturn: HeadersInit = {
    ...(accessToken ? { Authorization: 'Bearer ' + accessToken } : {}),
    ...(options?.headers || {}),
    ...xBrandId,
    'Accept-Language': options?.locale || 'en',
  };

  if (!isCheck)
    objectHeaderReturn = {
      'Content-Type': 'application/json',
      ...objectHeaderReturn,
    };
  return objectHeaderReturn;
}

function handleQueryParams(options?: ApiOptions): string {
  if (options?.locale || options?.queryParams) {
    let params = {} as { locale?: string };
    if (options?.locale) {
      params.locale = options.locale;
    }
    if (options.queryParams) {
      params = {
        ...options.queryParams,
        ...params,
      };
    }
    // NOTE: filter undefined keys
    return `?${stringify(params, { filter: (prefix, value) => value ?? undefined })}`;
  }
  return '';
}

export const Api = {
  get: async <T>(urlPath: string, options?: ApiOptions, urlSecond?: string): Promise<T> => {
    const apiPath = urlSecond || ApiService;
    let url = `${apiPath}${urlPath}${handleQueryParams(options)}`;
    if (options?.isExternalUrl) url = `${urlPath}${handleQueryParams(options)}`;

    if (options?.queryParams) url = decodeURIComponent(url);

    return fetch(`${url}`, {
      method: 'GET',
      cache: 'no-cache',
      headers: await handleHeaders(options),
    })
      .then(handleResponse)
      .catch(error => handleError(error));
  },
  post: async <T>(urlPath: string, data?: object, options?: ApiOptions, formData?: FormData, urlSecond?: string): Promise<T> => {
    const isCheckForm: boolean = formData ? true : false;
    const urlPost: string = urlSecond ? urlSecond : ApiService;
    return fetch(`${urlPost}${urlPath}${handleQueryParams(options)}`, {
      method: 'POST',
      cache: 'no-cache',
      body: isCheckForm ? formData : JSON.stringify(data),
      headers: await handleHeaders(options, isCheckForm),
    })
      .then(options?.isBlob ? handleResponseBlob : handleResponse)
      .catch(error => handleError(error));
  },
  put: async <T>(urlPath: string, data?: object, options?: ApiOptions, formData?: FormData): Promise<T> => {
    const isCheckForm: boolean = formData ? true : false;
    return fetch(`${ApiService}${urlPath}${handleQueryParams(options)}`, {
      method: 'PUT',
      cache: 'no-cache',
      body: isCheckForm ? formData : JSON.stringify(data),
      headers: await handleHeaders(options, isCheckForm),
    })
      .then(handleResponse)
      .catch(error => handleError(error));
  },
  patch: async <T>(urlPath: string, data?: object, options?: ApiOptions): Promise<T> => {
    return fetch(`${ApiService}${urlPath}${handleQueryParams(options)}`, {
      method: 'PATCH',
      cache: 'no-cache',
      body: JSON.stringify(data),
      headers: await handleHeaders(options),
    })
      .then(handleResponse)
      .catch(error => handleError(error));
  },
  delete: async <T>(urlPath: string, data?: Object, options?: ApiOptions): Promise<T> => {
    return fetch(`${ApiService}${urlPath}${handleQueryParams(options)}`, {
      method: 'DELETE',
      cache: 'no-cache',
      body: JSON.stringify(data),
      headers: await handleHeaders(options),
    })
      .then(handleResponse)
      .catch(error => handleError(error));
  },
};
